// src/components/OmniCommandBar.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  InputBase,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  AutoAwesome as SparkleIcon,
  Email as EmailIcon,
  Bolt as UrgentIcon,
  Mood as HappyIcon,
  MoodBad as AngryIcon,
  ArrowForward as EnterIcon,
  Handshake as HandshakeIcon,
  ScheduleSend as ScheduleSendIcon,
  LightbulbOutlined as TipsIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

// ---------------------------------------------
// 1. MAIN RUDE PHRASES LIST (long phrases)
// ---------------------------------------------
const RUDE_PHRASES = [
  "as i said", "like i said", "i already told you", "you didn't listen",
  "you are not listening", "obviously", "that's your fault",
  "that's not my problem", "we told you", "can you read", "did you read",
  "actually", "clearly", "anyway", "whatever", "fine then", "common sense",
  "educate yourself", "figure it out", "look it up", "google it",
  "not that hard", "simple logic", "use your brain",
  "do i have to explain everything", "are you serious", "pay attention",
  "try harder", "you should know this", "that’s basic knowledge",
  "if you bothered to read", "hurry up", "waste of time",
  "wasting my time", "are you done", "stop asking", "get to the point",
  "you are slow", "i don’t have time for this", "you are wrong",
  "you confused me", "you don't understand", "you fail to",
  "your mistake", "wrong again", "stop lying", "don't lie", "you messed up",
  "i don't care", "not my job", "not my fault", "so what",
  "who cares", "deal with it", "calm down", "relax", "whatever you say",
  "good luck with that", "sounds like a you problem",
  "i'm done here", "believe what you want", "i'm not repeating myself",
  "if you say so", "think before you ask", "that's ridiculous",
  "you make no sense", "fuck you",
];

// ------------------------------------------------------
// 2. SHORT / SINGLE-WORD TOXIC TERMS (regex safe)
// These MUST be tested with word boundaries.
// ------------------------------------------------------
const RUDE_SHORT_WORDS = [
  "k", "ok", "nah", "wtf", "stfu", "idiot", "dumb",
  "moron", "stupid", "crap", "damn", "shit", "fuck",
  "bitch", "asshole",
];

// Build regex patterns like:  \b(k|ok|nah)\b
const SHORT_RUDE_REGEX = new RegExp(`\\b(${RUDE_SHORT_WORDS.join("|")})\\b`, "i");

// ------------------------------------------------------
// 3. CLEANING FUNCTION — makes detection consistent
// ------------------------------------------------------
function normalizeMessage(msg) {
  return msg
    .toLowerCase()
    .replace(/[^\w\s]/g, " ") // remove punctuation
    .replace(/\s+/g, " ") // collapse double spaces
    .trim();
}

// ---------- SENTIMENT ENGINE (CUSTOMER ONLY) ----------
const analyzeSentiment = (messages) => {
  if (!messages || messages.length === 0) {
    return { label: "neutral", score: 0, isStrongNegative: false, sourceId: null };
  }

  // Only read CUSTOMER / PROVIDER mood (ignore consultant messages)
  const lastCustomerMsg = [...messages].reverse().find((m) => m.sender !== "consultant");
  if (!lastCustomerMsg || !lastCustomerMsg.text) {
    return {
      label: "neutral",
      score: 0,
      isStrongNegative: false,
      sourceId: lastCustomerMsg?.id || null,
    };
  }

  const text = lastCustomerMsg.text.toLowerCase();

  const negatives = [
    "angry", "delay", "cancel", "refund", "waiting", "bad", "slow", "hate",
    "unacceptable", "ridiculous", "trash", "stupid", "terrible", "horrible",
    "useless", "incompetent", "broken", "wrong", "mistake", "error", "fail",
    "mess", "disaster", "nightmare", "pathetic", "worst", "awful", "garbage",
    "appalling", "abysmal", "dreadful", "atrocious", "lousy", "subpar", "joke",
    "frustrated", "disappointed", "annoyed", "upset", "mad", "furious",
    "irate", "fed up", "tired", "disgusted", "unhappy", "shocked",
    "insulting", "embarrassing", "shameful", "stressful", "headache", "hassle",
    "scam", "fraud", "liar", "lying", "cheat", "cheated", "rip-off",
    "stole", "dishonest", "rude", "unprofessional", "robbery", "sketchy",
    "suspicious", "fake", "misleading",
    "waste", "forever", "ignoring", "ignored", "stuck", "silence",
    "ghosting", "no reply", "no answer", "urgent", "emergency", "crisis",
    "stranded", "missed", "lost", "damaged", "unsafe", "dangerous",
    "dirty", "filthy", "unhygienic", "smell", "noisy", "loud",
    "bedbugs", "cockroach", "insects", "cold", "freezing", "overbooked",
  ];

  const strongNegatives = [
    "this is ridiculous", "this is unacceptable", "im reporting", "i am reporting",
    "complain", "never again", "worst experience", "emergency", "i want a refund now",
    "speak to your manager", "get a supervisor", "speak to a human", "boss",
    "in charge", "ceo", "founder", "escalate this",
    "sue you", "lawyer", "attorney", "lawsuit", "legal action", "police",
    "authorities", "consumer protection", "court", "illegal",
    "chargeback", "bank", "fraud", "scam", "stole my money", "robbed",
    "theft", "compensation", "damages", "pay me back",
    "social media", "twitter", "facebook", "review", "bad review",
    "tell everyone", "expose", "viral",
    "stranded", "missed my flight", "hotel refused", "no driver",
    "cancel everything", "ruined my trip", "disaster",
  ];

  const positives = [
    "thanks", "thank you", "thx", "appreciate", "grateful", "bless you", "lifesaver",
    "great", "good", "amazing", "awesome", "fantastic", "wonderful", "excellent",
    "perfect", "brilliant", "super", "lovely", "nice", "cool", "wow", "satisfied",
    "outstanding", "impressive", "top notch",
    "happy", "excited", "love", "glad", "relieved", "relief", "can't wait",
    "looking forward", "dream come true",
    "fast", "quick", "prompt", "efficient", "smooth", "easy", "helpful",
    "best", "recommend", "pleasure", "seamless",
  ];

  let label = "neutral";
  let score = 0;
  let isStrongNegative = false;

  if (positives.some((w) => text.includes(w))) {
    label = "positive";
    score = 1;
  }

  if (negatives.some((w) => text.includes(w))) {
    label = "negative";
    score = -1;
  }

  if (strongNegatives.some((w) => text.includes(w))) {
    label = "negative";
    score = -2;
    isStrongNegative = true;
  }

  return { label, score, isStrongNegative, sourceId: lastCustomerMsg.id };
};

// ---------- FOLLOW-UP TEXT PARSER ----------
const parseFollowupText = (text) => {
  if (!text) return null;
  const lower = text.toLowerCase();

  // Expect things like: "/followup tomorrow 9am", "/followup monday 14:00"
  const timeMatch = lower.match(/(\d{1,2}(:\d{2})?\s*(am|pm)?)/);
  const dayMatch = lower.match(
    /(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/
  );

  const when = dayMatch ? dayMatch[0] : null;
  const time = timeMatch ? timeMatch[1] : null;

  if (!when && !time) {
    return { raw: text, when: null, time: null, ready: false };
  }

  return { raw: text, when, time, ready: true };
};

// ---------- TONE COACH (CATEGORIZED & PRESERVED) ----------
const analyzeConsultantTone = (textRaw = "") => {
  const text = textRaw.trim();
  if (!text || text.length < 1) {
    return { tone: "neutral", category: "generic", reasons: [] };
  }

  const reasons = [];
  let category = "generic";
  const clean = normalizeMessage(text);

  // 1. Check Short Toxic Terms (Regex)
  if (SHORT_RUDE_REGEX.test(clean)) {
    const match = clean.match(SHORT_RUDE_REGEX);
    if (match) {
      reasons.push(`"${match[0]}" sounds dismissive or unprofessional.`);
      category = "insult";
    }
  }

  // 2. Define Contextual Categories
  const CATEGORIES = {
    blame: ["your fault", "you didn't", "you fail", "wrong again", "stop lying", "you caused", "you messed up", "not my problem"],
    impatience: ["hurry up", "waste of time", "wasting my time", "are you done", "stop asking", "too long", "slow"],
    dismissive: ["whatever", "who cares", "so what", "deal with it", "figure it out", "not my job"],
    condescending: ["obviously", "can you read", "did you read", "educate yourself", "simple logic", "use your brain", "i said"],
    aggression: ["calm down", "relax", "shut up", "stfu", "hate", "stupid", "idiot", "damn", "hell", "fuck"],
  };

  // 3. Match Specific Categories
  Object.entries(CATEGORIES).forEach(([cat, phrases]) => {
    const foundPhrase = phrases.find((p) => clean.includes(p));
    if (foundPhrase) {
      category = cat;
      const feedback = {
        blame: "This sounds a bit accusatory.",
        impatience: "You seem frustrated with the speed.",
        dismissive: "This might make them feel ignored.",
        condescending: "Careful, this sounds a bit superior.",
        aggression: "This is too aggressive.",
        insult: "This is unprofessional.",
      };
      reasons.push(feedback[cat] || `Avoid using "${foundPhrase}".`);
    }
  });

  // 4. Fallback: Check the FULL original list (Catch-all)
  if (category === "generic") {
    RUDE_PHRASES.forEach((p) => {
      if (clean.includes(p)) {
        reasons.push(`Try a softer way to say "${p}".`);
      }
    });
  }

  // 5. Punctuation & Caps (The "Shouting" Check)
  if ((text.match(/!/g) || []).length >= 3) {
    reasons.push("Too many exclamations feel like yelling.");
    if (category === "generic") category = "aggression";
  }
  if (text.length > 4 && /[A-Z]{4,}/.test(text)) {
    reasons.push("All caps looks like you're shouting.");
    if (category === "generic") category = "aggression";
  }

  // 6. "Why didn't you" Check
  if (/^\s*(what|why)\s+didn'?t\s+you/i.test(text)) {
    reasons.push("Starting with 'Why didn't you' puts people on the defensive.");
    category = "blame";
  }

  const tone = reasons.length ? "harsh" : "neutral";
  return { tone, category, reasons };
};

// ---------- SUGGESTION BUILDER (Natural & Human) ----------
const buildProfessionalSuggestion = (rawText, customerName, lastCustomerMessage, category, customerSentimentLabel) => {
  const name = customerName || "there";

  const neutralOpeners = [
    `Hi ${name}, thanks for the message.`,
    `Hi ${name}, thanks for getting back to me.`,
    `Hi ${name},`,
  ];

  const negativeOpeners = [
    `Hi ${name}, I hear you, and I want to sort this out.`,
    `Hi ${name}, I totally get where you're coming from.`,
    `Hi ${name}, thanks for being patient with this.`,
    `Hi ${name}, I can see why that's frustrating.`,
  ];

  const openers = customerSentimentLabel === "negative" ? negativeOpeners : neutralOpeners;
  const greeting = openers[Math.floor(Math.random() * openers.length)];

  let body = "Let's get this back on track.";

  switch (category) {
    case "blame":
      body = "I think wires got crossed here. Let's double-check the details to make sure we're on the same page.";
      break;
    case "impatience":
      body = "I know this is taking a bit longer than expected. I'm chasing this up right now to get you an answer.";
      break;
    case "dismissive":
      body = "While I can't fix this directly right now, I don't want to leave you hanging. Let me connect you with the right person.";
      break;
    case "condescending":
      body = "Sorry if my last explanation was a bit confusing. Let me try to break it down more clearly.";
      break;
    case "aggression":
    case "insult":
      body = "Let's take a step back. I really want to help you solve this, so let's focus on the solution.";
      break;
    default:
      if (rawText.toLowerCase().includes("told you")) body = "Just to circle back to what we discussed earlier—here is the plan.";
      else body = "Let's clarify the situation so we can move forward.";
      break;
  }

  let contextLine = "";
  if (lastCustomerMessage && lastCustomerMessage.length > 3) {
    const snippet = lastCustomerMessage.trim().slice(0, 40);
    contextLine = `\n\n(Regarding "${snippet}...")`;
  }

  const closings = [
    "\n\nDoes that sound fair?",
    "\n\nLet me know if that helps!",
    "\n\nI'll keep you posted.",
  ];
  const closing = closings[Math.floor(Math.random() * closings.length)];

  return `${greeting}\n\n${body}${contextLine}${closing}`;
};

// ---------- COMMAND / INTENT DICTIONARY (UPDATED: /ask REMOVED) ----------
const INTENT_DICTIONARY = [
  {
    id: "email_chase",
    triggers: ["email", "follow up", "followup", "chase", "remind"],
    command: "/email_followup",
    desc: "Send follow-up email",
    icon: <EmailIcon />,
    smartArg: () => null,
  },
  {
    id: "mark_urgent",
    triggers: ["urgent", "asap", "emergency", "fast"],
    command: "/urgent",
    desc: "Mark priority: High",
    icon: <UrgentIcon />,
    smartArg: () => null,
  },
  {
    id: "handoff",
    triggers: ["handoff", "hand over", "transfer", "escalate"],
    command: "/handoff",
    desc: "Hand this chat to another consultant",
    icon: <HandshakeIcon />,
    smartArg: () => null,
  },
  {
    id: "follow_up",
    triggers: ["follow up", "followup", "remind tomorrow", "remind me"],
    command: "/followup",
    desc: "Schedule a follow-up reminder",
    icon: <ScheduleSendIcon />,
    smartArg: (text) => parseFollowupText(text),
  },
];

// ---------- COMPONENT ----------
export default function OmniCommandBar({
  onExecute,
  messages,
  customerName,
  onSentimentChange,
  onAutoUrgent,
}) {
  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIntent, setActiveIntent] = useState(null);
  const [smartArgs, setSmartArgs] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);

  const [sentiment, setSentiment] = useState({ label: "neutral", score: 0 });
  const lastAutoUrgentSourceRef = useRef(null);
  const [toneState, setToneState] = useState({ tone: "neutral", category: "generic", reasons: [] });
  const [suggestedReply, setSuggestedReply] = useState(null);

  // ----- Watch messages → Sentiment + auto-urgent -----
  useEffect(() => {
    const s = analyzeSentiment(messages);
    const formatted = { label: s.label, score: s.score };
    setSentiment(formatted);

    if (typeof onSentimentChange === "function") onSentimentChange(formatted);

    if (
      s.label === "negative" &&
      s.isStrongNegative &&
      s.sourceId &&
      typeof onAutoUrgent === "function" &&
      lastAutoUrgentSourceRef.current !== s.sourceId
    ) {
      lastAutoUrgentSourceRef.current = s.sourceId;
      onAutoUrgent();
    }
  }, [messages, onSentimentChange, onAutoUrgent]);

  // ----- Watch input → Intent detection -----
  useEffect(() => {
    const value = input;
    const trimmed = value.trim();

    if (!trimmed) {
      setActiveIntent(null);
      setSmartArgs(null);
      setIsOpen(false);
      return;
    }

    const lower = trimmed.toLowerCase();

    if (lower.startsWith("/")) {
      const matchedFromSlash = INTENT_DICTIONARY.find((intent) =>
        lower.startsWith(intent.command)
      );

      if (matchedFromSlash) {
        setIsOpen(false);
        setActiveIntent(matchedFromSlash);
        const extracted = matchedFromSlash.smartArg ? matchedFromSlash.smartArg(value) : null;
        setSmartArgs(extracted || null);
      } else {
        setActiveIntent(null);
        setSmartArgs(null);
        setIsOpen(true);
      }
      return;
    }

    const foundIntent = INTENT_DICTIONARY.find((intent) =>
      intent.triggers.some((trigger) => lower.includes(trigger))
    );

    if (foundIntent) {
      setActiveIntent(foundIntent);
      const extracted = foundIntent.smartArg ? foundIntent.smartArg(value) : null;
      setSmartArgs(extracted || null);
      if (extracted?.ready) setIsOpen(false);
    } else {
      setActiveIntent(null);
      setSmartArgs(null);
      setIsOpen(false);
    }
  }, [input]);

  // ----- Tone coach -----
  useEffect(() => {
    const analysis = analyzeConsultantTone(input);
    setToneState(analysis);

    if (analysis.tone === "harsh") {
      const lastCustomer =
        [...(messages || [])].reverse().find((m) => m.sender !== "consultant")?.text || "";

      const suggestion = buildProfessionalSuggestion(
        input,
        customerName,
        lastCustomer,
        analysis.category,
        sentiment.label
      );
      setSuggestedReply(suggestion);
      return;
    }

    if (analysis.tone === "neutral" && !activeIntent) {
      setSuggestedReply(null);
    }
  }, [input, messages, customerName, sentiment.label, activeIntent]);

  // ----- Smart suggestions (For Intents) -----
  useEffect(() => {
    if (toneState.tone === "harsh") return;
    if (isDismissed) return;

    let suggestion = null;

    if (sentiment.label === "negative") {
      suggestion = `Hi ${customerName || "there"}, I'm really sorry about the frustration. I want to help sort this out immediately. Let me check the details and get right back to you.`;
    } else if (activeIntent?.id === "follow_up") {
      const whenText =
        smartArgs && smartArgs.ready
          ? `${smartArgs.when || "tomorrow"} at ${smartArgs.time || "09:00"}`
          : "soon";
      suggestion = `Hi ${customerName || "there"}, just wanted to let you know I will follow up with you ${whenText} with an update. Talk soon!`;
    } else if (activeIntent?.id === "handoff") {
      suggestion = `Hi ${customerName || "there"}, I'm going to connect you with a colleague who specializes in this. They'll be with you in just a moment to help further.`;
    } else {
      suggestion = null;
    }

    if (suggestion) setSuggestedReply(suggestion);
  }, [toneState.tone, sentiment, activeIntent, smartArgs, customerName, isDismissed]);

  const handleInsertSuggestion = () => {
    if (!suggestedReply) return;
    setInput((prev) => (prev ? `${prev}\n\n${suggestedReply}` : suggestedReply));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      execute();
    }
    if (e.key === "ArrowDown" && isOpen) {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % INTENT_DICTIONARY.length);
    }
  };

  // ----- Execute send / command -----
  const execute = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    // 1) Smart intents with args
    if (activeIntent && smartArgs && smartArgs.ready) {
      if (activeIntent.id === "follow_up") {
        onExecute("command", trimmed);
      } else {
        onExecute("command", activeIntent.command);
      }
      setInput("");
      return;
    }

    // 2) Intent with no extra args
    if (activeIntent && !smartArgs) {
      if (activeIntent.id === "handoff") {
        onExecute("command", "/handoff");
      } else {
        onExecute("command", trimmed.startsWith("/") ? trimmed : activeIntent.command);
      }
      setInput("");
      return;
    }

    // 3) Manual Command
    if (trimmed.startsWith("/")) {
      onExecute("command", trimmed);
      setInput("");
      return;
    }

    // 4) Normal message
    onExecute("message", trimmed);
    setInput("");
  };

  const getBorderColor = () => {
    if (toneState.tone === "harsh") return "#ff9800";
    if (activeIntent) return "#9c27b0";
    if (sentiment.label === "negative") return "#f44336";
    if (sentiment.label === "positive") return "#4caf50";
    if (isOpen) return "#2db838";
    return "#e0e0e0";
  };

  const getShadow = () => {
    if (toneState.tone === "harsh") return "0 0 10px rgba(255, 152, 0, 0.25)";
    if (sentiment.label === "negative") return "0 0 10px rgba(244, 67, 54, 0.3)";
    if (activeIntent) return "0 0 10px rgba(156, 39, 176, 0.3)";
    return "none";
  };

  const placeholderText =
    sentiment.label === "negative"
      ? "De-escalate the situation..."
      : `Message ${customerName || "Customer"}...`;

  return (
    <Box sx={{ position: "relative", width: "100%", zIndex: 10 }}>
      {/* NEGATIVE MOOD WARNING CHIP */}
      <AnimatePresence>
        {sentiment.label === "negative" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ position: "absolute", top: -30, right: 0 }}
          >
            <Chip
              icon={<AngryIcon style={{ color: "white" }} />}
              label="Customer seems frustrated"
              size="small"
              sx={{ bgcolor: "#f44336", color: "white", fontWeight: "bold", pointerEvents: "none" }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* SUGGESTION BOX (Ghost text) */}
      <AnimatePresence>
        {suggestedReply && (
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            style={{ position: "absolute", bottom: "115%", left: 0, right: 0 }}
          >
            <Paper
              elevation={6}
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                borderLeft: `4px solid ${toneState.tone === "harsh" ? "#ff9800" : "#2db838"}`,
                bgcolor: "rgba(255,255,255,0.96)",
                backdropFilter: "blur(6px)",
              }}
            >
              <TipsIcon
                sx={{
                  color: toneState.tone === "harsh" ? "#ff9800" : "#2db838",
                  mt: 0.3,
                  flexShrink: 0,
                }}
              />

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Suggested reply
                  </Typography>
                  {toneState.tone === "harsh" && (
                    <Chip size="small" label="Tone coach" sx={{ height: 18, fontSize: "0.6rem" }} />
                  )}
                </Box>

                {toneState.tone === "harsh" && toneState.reasons[0] && (
                  <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 0.5 }}>
                    {toneState.reasons[0]}
                  </Typography>
                )}

                <Typography
                  variant="caption"
                  sx={{
                    color: "text.primary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 300,
                    overflowY: "auto",
                    mt: 1,
                    lineHeight: 1.5,
                    pr: 1,
                  }}
                >
                  {suggestedReply}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
                <Tooltip title="Dismiss">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSuggestedReply(null);
                      setIsDismissed(true);
                    }}
                    sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <ButtonSmall onClick={handleInsertSuggestion} />
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INTENT PREVIEW (Einstein card) */}
      <AnimatePresence>
        {activeIntent && smartArgs && (
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            style={{
              position: "absolute",
              bottom: suggestedReply ? "210%" : "115%",
              left: 0,
              right: 0,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderLeft: `4px solid ${smartArgs.ready ? "#2db838" : "#ff9800"}`,
                bgcolor: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(5px)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SparkleIcon sx={{ color: "#9c27b0" }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {smartArgs.ready ? "Ready to execute" : "Analysing intent..."}
                  </Typography>
                  {activeIntent.id === "follow_up" && (
                    <Typography variant="caption" color="text.secondary">
                      {smartArgs.ready
                        ? `Follow-up detected: ${smartArgs.when || "soon"} ${smartArgs.time || ""}`
                        : "Add when (e.g. tomorrow 9am) to schedule follow-up"}
                    </Typography>
                  )}
                </Box>
              </Box>

              {smartArgs.ready && (
                <Chip label="Press ENTER" size="small" color="success" icon={<EnterIcon />} />
              )}
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SLASH COMMAND MENU */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{
              position: "absolute",
              bottom: "110%",
              left: 0,
              right: 0,
              marginBottom: 8,
            }}
          >
            <Paper elevation={6} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <List dense>
                {INTENT_DICTIONARY.map((cmd, index) => (
                  <ListItem
                    key={cmd.id}
                    button
                    selected={index === selectedIndex}
                    onClick={() => {
                      setInput(cmd.command + " ");
                      setIsOpen(false);
                    }}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "primary.light",
                        color: "white",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: index === selectedIndex ? "white" : "inherit" }}>
                      {cmd.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={cmd.command}
                      secondary={cmd.desc}
                      secondaryTypographyProps={{
                        color: index === selectedIndex ? "rgba(255,255,255,0.8)" : "textSecondary",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MAIN INPUT BAR */}
      <Paper
        elevation={activeIntent || toneState.tone === "harsh" ? 4 : 1}
        sx={{
          p: "2px 4px",
          display: "flex",
          alignItems: "center",
          borderRadius: 3,
          border: `2px solid ${getBorderColor()}`,
          boxShadow: getShadow(),
          transition: "all 0.3s ease",
          position: "relative",
        }}
      >
        <Box sx={{ p: 1, color: "action.active" }}>
          {sentiment.label === "negative" ? (
            <AngryIcon sx={{ color: "#f44336", animation: "pulse 1s infinite" }} />
          ) : sentiment.label === "positive" ? (
            <HappyIcon sx={{ color: "#4caf50" }} />
          ) : activeIntent ? (
            <SparkleIcon sx={{ color: "#9c27b0", animation: "spin 2s linear infinite" }} />
          ) : (
            <Box sx={{ fontSize: 18, fontWeight: "bold", px: 1, color: "text.disabled" }}>
              {">"}
            </Box>
          )}
        </Box>

        <InputBase
          sx={{ flex: 1, ml: 1 }}
          placeholder={placeholderText}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />

        <Box sx={{ p: 0.5 }}>
          <Tooltip title="Send">
            <IconButton
              size="small"
              onClick={execute}
              sx={{
                bgcolor:
                  toneState.tone === "harsh"
                    ? "#ff9800"
                    : sentiment.label === "negative"
                    ? "#f44336"
                    : activeIntent
                    ? "#9c27b0"
                    : input.trim()
                    ? "primary.main"
                    : "grey.300",
                color: "white",
                "&:hover": {
                  bgcolor:
                    toneState.tone === "harsh"
                      ? "#fb8c00"
                      : sentiment.label === "negative"
                      ? "#d32f2f"
                      : "primary.dark",
                },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <style>{`
        @keyframes pulse { 
          0% { opacity: 1; } 
          50% { opacity: 0.5; } 
          100% { opacity: 1; } 
        }
        @keyframes spin { 
          100% { transform: rotate(360deg); } 
        }
      `}</style>
    </Box>
  );
}

function ButtonSmall({ onClick }) {
  const handleClick = () => {
    if (typeof onClick === "function") onClick();
  };

  return (
    <Tooltip title="Insert this reply into the chat box">
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          bgcolor: "primary.main",
          color: "white",
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        <EnterIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
